// Punto de venta · emitir sin pago (pedido de Rodrigo, 2026-10-27): la caja nunca emite una boleta,
// factura o vale sin su pago por su cuenta, aunque la nota sea a crédito. Con saldo, la cajera cobra
// o pide la autorización EN LÍNEA: aparece en Firmas para quien tiene ventas.documento.sin_pago, ella
// espera en pantalla (se consulta cada 3 s) y, firmada, el documento sale solo. Si la rechazan, lo ve
// aquí con quién y su comentario. Sin motivo obligatorio: queda quién pidió, quién firmó y cuándo.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { AutorizacionSinPago, FilaSinPago } from '../api/contratos';
import { Modal } from '../comun/Modal';
import { pesos } from '../comun/formato';

/** La última autorización de la nota; mientras se espera, se consulta cada 3 s. */
export function useAutorizacionSinPago(
  empresaId: string,
  notaId: string | null,
  esperando: boolean,
) {
  return useQuery({
    queryKey: ['emp', empresaId, 'ventas', 'sin-pago', notaId ?? ''] as const,
    queryFn: () =>
      api.get<{ autorizacion: AutorizacionSinPago | null }>(`/ventas/notas/${notaId}/sin-pago`),
    enabled: !!notaId,
    staleTime: 2_000,
    // Esperando en pantalla: cada 3 s. Si la cajera siguió trabajando, cada 10 s mientras siga
    // pendiente (al firmarse aparece «Emitir · sin pago autorizado»).
    refetchInterval: (q) =>
      esperando ? 3_000 : q.state.data?.autorizacion?.estado === 'PENDIENTE' ? 10_000 : false,
  });
}

/** Firmas y tesorería: pendientes y resueltas (quién pidió, quién firmó y qué documento salió). */
export function useFirmasSinPago(
  empresaId: string,
  filtro: { estado?: string; desde?: string },
  activo = true,
  refrescarCada: number | false = 10_000,
) {
  return useQuery({
    queryKey: ['emp', empresaId, 'ventas', 'sin-pago', 'lista', filtro] as const,
    queryFn: () =>
      api.get<{ items: FilaSinPago[] }>('/ventas/sin-pago', {
        query: { empresaId, ...filtro },
      }),
    enabled: activo,
    refetchInterval: refrescarCada,
    staleTime: 5_000,
  });
}

const NOMBRE_DOC: Record<string, string> = { BOLETA: 'boleta', FACTURA: 'factura', VALE: 'vale' };

/** La cajera espera la firma con el diálogo abierto. */
export function EsperandoSinPago({
  numeroVisible,
  autorizacion,
  retirando,
  alRetirar,
  alCerrar,
}: {
  numeroVisible: string;
  autorizacion: AutorizacionSinPago | null;
  retirando: boolean;
  alRetirar: () => void;
  alCerrar: () => void;
}) {
  const [segundos, setSegundos] = useState(0);
  const estado = autorizacion?.estado ?? 'PENDIENTE';
  const resuelta = estado === 'RECHAZADA' || estado === 'RETIRADA';
  useEffect(() => {
    if (resuelta) return;
    const t = setInterval(() => setSegundos((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [resuelta]);
  const espera = `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
  return (
    <Modal
      key={resuelta ? 'resuelta' : 'esperando'}
      titulo={`Emitir sin pago · ${numeroVisible}`}
      ancho="angosto"
      ubicacion="media"
      alCerrar={alCerrar}
      pie={
        resuelta ? (
          <button type="button" className="primario" data-autofoco onClick={alCerrar}>
            Listo (Enter)
          </button>
        ) : (
          <>
            <button type="button" disabled={retirando || !autorizacion} onClick={alRetirar}>
              Retirar la solicitud
            </button>
            <button type="button" data-autofoco onClick={alCerrar}>
              Seguir trabajando (queda pendiente)
            </button>
          </>
        )
      }
    >
      {estado === 'RECHAZADA' ? (
        <div className="firma-resultado tono-malo" data-sin-pago="RECHAZADA">
          <p className="firma-titulo">Rechazada: no se emite sin pago</p>
          <p>{autorizacion?.resueltaPorNombre ?? 'El supervisor'} no autorizó emitir sin pago.</p>
          {autorizacion?.comentario && <p>«{autorizacion.comentario}»</p>}
          <p className="apagado">Cobra la nota (F10) para emitir el documento.</p>
        </div>
      ) : estado === 'RETIRADA' ? (
        <div className="firma-resultado" data-sin-pago="RETIRADA">
          <p className="firma-titulo">La solicitud se retiró</p>
        </div>
      ) : (
        <div className="firma-esperando" aria-live="polite" data-sin-pago="PENDIENTE">
          <p className="firma-titulo">
            <span className="girando" aria-hidden="true" /> Esperando la firma del supervisor…
          </p>
          {autorizacion && (
            <p>
              Emitir {NOMBRE_DOC[autorizacion.documento] ?? 'el documento'} sin pago por{' '}
              <b>{pesos(autorizacion.monto)}</b>.
            </p>
          )}
          <p className="apagado">
            El supervisor la ve en «Firmas». Esperando {espera} · al autorizarla, el documento sale
            solo.
          </p>
        </div>
      )}
    </Modal>
  );
}
